import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchHardstuckPlayers } from '../services/LeagueStatsService';
import { getLatestSeason } from '../services/MatchService';
import Loading from '../components/Loading';
import PlayerImage from '../components/PlayerImage';
import { Anchor } from '@mui/icons-material';
import styles from '../styles/StatsView.module.css';

interface HardstuckPlayer {
    rank: number;
    steamId: string;
    playerName: string;
    currentTeam: string;
    divisionName: string;
    divisionLevel: number;
    seasonsInDivision: number;
    firstSeason: number;
    lastSeason: number;
    totalGamesPlayed: number;
    seasons: number[];
    explanation: string;
}

interface HardstuckData {
    title: string;
    subtitle: string;
    currentSeason: number;
    minSeasons: number;
    players: HardstuckPlayer[];
    generatedAt: string;
}

const LeagueStatsHardstuckPage = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<HardstuckData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [displayCount, setDisplayCount] = useState<number>(10);
    const [minSeasons, setMinSeasons] = useState<number>(4);
    const [division, setDivision] = useState<string>('all');
    const [currentSeason, setCurrentSeason] = useState<number>(0);
    const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);

    useEffect(() => {
        const init = async () => {
            try {
                const latest = await getLatestSeason();
                setCurrentSeason(latest);
                const seasons = [];
                for (let s = 18; s <= latest; s++) {
                    seasons.push(s);
                }
                setAvailableSeasons(seasons.reverse());
            } catch (err) {
                console.error('Error fetching latest season:', err);
                setCurrentSeason(31);
            }
        };
        init();
    }, []);

    useEffect(() => {
        if (currentSeason > 0) loadData();
    }, [minSeasons, division, currentSeason]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchHardstuckPlayers(currentSeason, minSeasons, division);
            setData(response);
        } catch (err) {
            console.error('Error loading hardstuck data:', err);
            setError('Kunne ikke hente data');
        } finally {
            setLoading(false);
        }
    };

    const formatDivision = (div: string) => {
        // Handle both full division names and filter values
        const divLower = div.toLowerCase();
        
        if (divLower === 'all') return 'Alle Divisioner';
        if (divLower === 'ligaen' || div.includes('Ligaen')) return 'Ligaen';
        if (divLower === '1div' || div.includes('1. Division')) return '1. Division';
        if (divLower === '2div' || div.includes('2. Division')) return '2. Division';
        if (divLower === '3div' || div.includes('3. Division')) return '3. Division';
        if (divLower === '4div' || div.includes('4. Division')) return '4. Division';
        if (div.includes('5. Division')) return '5. Division';
        
        return div;
    };

    const getDivisionColor = (divisionName: string) => {
        if (divisionName.includes('Ligaen')) return '#ffd700';
        if (divisionName.includes('1.')) return '#c0c0c0';
        if (divisionName.includes('2.')) return '#cd7f32';
        if (divisionName.includes('3.')) return '#6c757d';
        if (divisionName.includes('4.')) return '#495057';
        if (divisionName.includes('5.')) return '#343a40';
        return '#6c757d';
    };

    const getRankClass = (index: number) => {
        switch(index) {
            case 0: return styles.rank1;
            case 1: return styles.rank2;
            case 2: return styles.rank3;
            default: return styles.rankOther;
        }
    };

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    if (!data) {
        return <div className="text-center mt-5 text-white">No data available</div>;
    }

    return (
        <div className={styles.pageContainer}>
            <div className={styles.twitterCard}>
                {/* Compact Header */}
                <div className={styles.compactHeader}>
                    <div className={styles.headerContent}>
                        <div className={styles.titleSection}>
                            <h1 className={styles.mainTitle}>Hardstuck Spillere</h1>
                            <div className={styles.subtitle}>
                                {division === 'all' ? 'Spillere fanget i samme division' : `Hardstuck i ${formatDivision(division)}`}
                            </div>
                        </div>
                        <div className={styles.seasonBadge}>Sæson {currentSeason}</div>
                    </div>
                </div>

                {/* Single Column Layout */}
                <div className={styles.singleColumnContainer}>
                    <div className={styles.columnHeader}>
                        <div className={`${styles.columnIcon}`} style={{background: 'linear-gradient(135deg, rgba(108, 117, 125, 0.2), rgba(108, 117, 125, 0.3))'}}>
                            <Anchor style={{ color: '#6c757d' }} />
                        </div>
                        <div className={styles.columnTitle}>
                            <h3>Mest Hardstuck Spillere</h3>
                            <div className={styles.columnSubtitle}>Minimum {minSeasons} sæsoner i samme division</div>
                        </div>
                    </div>

                    <div className={styles.playerList}>
                        {data.players.slice(0, displayCount).map((player, index) => (
                            <div key={player.steamId} className={`${styles.playerCard}`}>
                                <div className={`${styles.rankBadge} ${getRankClass(index)}`}>
                                    {player.rank}
                                </div>
                                <div className={styles.playerImage}>
                                    <PlayerImage 
                                        id={player.steamId} 
                                        width={50}
                                    />
                                </div>
                                <div className={styles.playerInfo} style={{ flex: 2 }}>
                                    <Link to={`/player/${player.steamId}`} className={styles.playerName}>
                                        {player.playerName}
                                    </Link>
                                    <div className={styles.teamInfo}>
                                        <span>{player.currentTeam || 'Ingen hold'}</span>
                                        <span 
                                            className={styles.divisionBadge}
                                            style={{ backgroundColor: getDivisionColor(player.divisionName) + '33', color: getDivisionColor(player.divisionName) }}
                                        >
                                            {formatDivision(player.divisionName)}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.ratingStats}>
                                    <div className={styles.changeValue} style={{ color: '#6c757d', fontSize: '1.8rem' }}>
                                        {player.seasonsInDivision}
                                    </div>
                                    <div className={styles.ratingValues}>
                                        S{player.firstSeason} - S{player.lastSeason} • {player.totalGamesPlayed} kampe
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Season badges for top 3 */}
                    {data.players.slice(0, 3).map((player, index) => (
                        <div key={`detail-${player.steamId}`} style={{ 
                            marginTop: '15px', 
                            padding: '12px 20px',
                            background: 'rgba(255, 255, 255, 0.03)',
                            borderRadius: '8px',
                            borderLeft: `3px solid ${getDivisionColor(player.divisionName)}`
                        }}>
                            <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem', marginBottom: '8px' }}>
                                <strong>{player.playerName}</strong>: {player.explanation}
                            </div>
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem', marginRight: '5px' }}>Sæsoner:</span>
                                {player.seasons.map((season, idx) => (
                                    <span key={idx} style={{
                                        background: 'rgba(241, 92, 37, 0.2)',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        color: 'rgba(255, 255, 255, 0.7)'
                                    }}>
                                        S{season}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Info Box */}
                <div className={styles.infoBox}>
                    <strong>Hardstuck:</strong> Spillere der har været i samme division i mindst {minSeasons} sæsoner siden sæson 18. 
                    Disse spillere har vist konsistent præstation i deres division over længere tid.
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <div className={styles.footerText}>
                        <span className={styles.highlight}>PowerStats.dk</span> • Liga Historik Analyse • Sæson {currentSeason}
                    </div>
                </div>

                {/* Filter Controls - Hidden at bottom */}
                <div className={styles.filterControls}>
                    <div className={styles.filterBar}>
                        <select
                            className={styles.filterSelect}
                            value={currentSeason}
                            onChange={(e) => setCurrentSeason(Number(e.target.value))}
                        >
                            {availableSeasons.map(s => (
                                <option key={s} value={s}>Sæson {s}</option>
                            ))}
                        </select>
                        <select
                            className={styles.filterSelect}
                            value={division}
                            onChange={(e) => setDivision(e.target.value)}
                        >
                            <option value="all">Alle Divisioner</option>
                            <option value="ligaen">Ligaen</option>
                            <option value="1div">1. Division</option>
                            <option value="2div">2. Division</option>
                            <option value="3div">3. Division</option>
                            <option value="4div">4. Division</option>
                        </select>
                        <select 
                            className={styles.filterSelect}
                            value={minSeasons}
                            onChange={(e) => setMinSeasons(Number(e.target.value))}
                        >
                            <option value={3}>Minimum 3 sæsoner</option>
                            <option value={4}>Minimum 4 sæsoner</option>
                            <option value={5}>Minimum 5 sæsoner</option>
                            <option value={6}>Minimum 6 sæsoner</option>
                        </select>
                        <select 
                            className={styles.filterSelect}
                            value={displayCount}
                            onChange={(e) => setDisplayCount(Number(e.target.value))}
                        >
                            <option value={5}>Vis Top 5</option>
                            <option value={10}>Vis Top 10</option>
                            <option value={15}>Vis Top 15</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeagueStatsHardstuckPage;